'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

/* ─── WAVEFORM COMPONENT ─── */
function WaveformVisualizer() {
  const heights = [
    28, 44, 20, 56, 36, 64, 24, 48, 32, 60, 40, 52, 28, 44, 36, 56, 20, 48, 32, 64, 40, 28, 52, 36,
  ];
  return (
    <div className="flex items-end gap-1 h-16">
      {heights.map((h, i) => (
        <span key={i} className="waveform-bar" style={{ height: `${h}px` }} />
      ))}
    </div>
  );
}

/* ─── SERVICE DATA ─── */
const services = [
  {
    id: 'mixing',
    label: '01 / Mixing & Mastering',
    title: 'Mixing & Mastering',
    description:
      'Clear, punchy and high quality metal mixes tailored to ensure you mixes deliver the depth they deserve whilst also competing with the best',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
      </svg>
    ),

    large: false,
  },
  {
    id: 'drums',
    label: '02 / Session Drumming',
    title: 'Session Drumming',
    description:
      'Live or studio session drums for any genre. Drums ranging from blast beats, complex patterns, odd meters and high tempos. All delivered to the highest professional standard.',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="12" cy="12" rx="10" ry="5" />
        <path d="M2 12v5c0 2.76 4.48 5 10 5s10-2.24 10-5v-5" />
        <path d="M12 7v10" />
      </svg>
    ),

    large: false,
  },
  {
    id: 'post',
    label: '03 / Post Production',
    title: 'Post Production',
    description:
      'Need your recorded tracks edited, comped, tuned or cleaned up? Need an adittional layer of synths to help lift your choruses? I provide industry standard post production for any metal track!',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>
    ),

    large: false,
  },
];

/* ─── PORTFOLIO DATA ─── */
const portfolioWorks = [
  {
    title: '(Not in your) Nature',
    band: 'Covert UK',
    role: 'Mix & Master',
    genre: 'Nu Metal, Metalcore',
    year: '2025',
    image: '/assets/images/9-1773171512877.png',
    alt: 'Album artwork for (Not in your) Nature by Covert UK',
    youtubeUrl: 'https://www.youtube.com/watch?v=x_dY3-tt_-M',
  },
  {
    title: 'Twisted Mind',
    band: 'Covert UK',
    role: 'Recording + Mix',
    genre: 'Metalcore, Hardcore',
    year: '2025',
    image: '/assets/images/10-1773171732469.png',
    alt: 'Album artwork for Twisted Mind by Covert UK',
    youtubeUrl: 'https://www.youtube.com/watch?v=ozgr6Tbhzvw',
  },
  {
    title: 'Altar',
    band: 'Covert UK',
    role: 'Recording + Mix',
    genre: 'Metalcore, Hardcore',
    year: '2025',
    image: '/assets/images/Screenshot_2026-03-10_at_19.35.22-1773171951618.png',
    alt: 'Altar by Covert UK',
    youtubeUrl: 'https://www.youtube.com/watch?v=2l6OQFlcLWw',
  },
  {
    title: 'Nothing More Drum Playthrough',
    band: 'Dystopian Sun',
    role: 'Session Drums',
    genre: 'Deathcore',
    year: '2025',
    image: '/assets/images/Untitled_design-1773172263226.png',
    alt: 'Dystopian Sun - Nothing More Drum Playthrough',
    youtubeUrl: 'https://www.youtube.com/watch?v=3nU6wFoxTxo',
  },
];

/* ─── TESTIMONIALS ─── */
interface Testimonial {
  quote: string;
  name: string; // band / artist name
  band: string; // work done with them
  logo?: string; // optional band logo/photo; falls back to the name initial
}

const testimonials: Testimonial[] = [
  {
    quote:
      "Having Diggity Glover as our sound and production engineer for the mixing of our records has been nothing but positive. The way he took the recorded tracks and made them into what they are now — making us sound as professional and well-produced as our influences. Having him mix us was the best choice we've ever made as a band.",
    name: 'Covert UK',
    band: 'Songwriting · Recording · Production · Mixing',
  },
  {
    quote:
      "What really sets him apart is how he respectfully adds his own input where it counts. His suggestions and creative touches have genuinely helped elevate both our live performances and spilled into recording decisions. If you're looking for a reliable and talented metal drummer who understands how to serve the song while still bringing great ideas to the table, Diggs is a fantastic choice.",
    name: 'Wosh',
    band: 'Session Drumming',
  },
  {
    quote:
      "I've done multiple sessions with Diggity for both writing and recording, and I cannot express enough how welcoming and easy it is to work with him. When it comes to getting your final product exactly how you imagine it, Diggity is the best — he took exactly what I described and turned it into reality, not just with the mixing and mastering but the writing and recording too. His turnaround time is extremely fast and the results are beyond incredible.",
    name: 'Codex Gigax',
    band: 'Mixing · Mastering · Songwriting',
  },
  {
    quote:
      "Diggity did the sound for us at Sunbird in Darwen and made the whole thing easy. Everything ran smoothly, the sound was spot on, and he was great to work with throughout. We'd definitely recommend him and look forward to working with him again.",
    name: 'Dwelled',
    band: 'Live Sound Mixing',
  },
];

/* ─── CREDITS MARQUEE ─── */
const creditsBand = ['Covert UK', 'Dystopian Sun', 'Codex Gigax', 'Wosh'];

/* ─── SCROLL REVEAL HOOK ─── */
function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function Homepage() {
  useScrollReveal();

  const [monthYear, setMonthYear] = useState('March 2026');

  useEffect(() => {
    const updateMonthYear = () => {
      const now = new Date();
      const month = now.toLocaleString('default', { month: 'short' });
      const year = now.getFullYear();
      setMonthYear(`${month} ${year}`);
    };
    updateMonthYear();
  }, []);

  return (
    <div className="min-h-screen bg-bg-base text-fg-base overflow-x-hidden">
      <Header />

      {/* ─── GRID OVERLAY ─── */}
      <div className="grid-overlay">
        <div className="grid-inner">
          <div className="grid-line-v" />
          <div className="grid-line-v hidden md:block" />
          <div className="grid-line-v hidden lg:block" />
          <div className="grid-line-v" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
              HERO SECTION — Massive Typography + Atmospheric Depth
           ═══════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-16 overflow-hidden">
        {/* Background video — loops silently, falls back to photo */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            disablePictureInPicture
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ filter: 'grayscale(100%) brightness(0.45)' }}
          >
            <source src="https://baxwbiwdwyhamsmlrffg.supabase.co/storage/v1/object/public/media/hero-bg.mp4" type="video/mp4" />
            {/* Fallback image if video can't play */}
            <Image
              src="/assets/images/diggity-1.jpg"
              alt="Diggity Glover playing drums live"
              fill
              priority
              sizes="100vw"
              className="object-cover object-center"
            />
          </video>
          <div className="absolute inset-0 bg-black/55" />
          {/* Video credit */}
          <a
            href="https://www.rjfhmusic.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '16px',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.35)',
              textDecoration: 'none',
              zIndex: 5,
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
          >
            Filmed by RJFH Music
          </a>
        </div>

        {/* Atmospheric glow layers */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-1/4 left-1/2 w-[800px] h-[400px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(204,0,0,0.14) 0%, transparent 70%)',
              filter: 'blur(60px)',
              transform: 'translateX(-50%)',
              animation: 'glow-a 16s ease-in-out infinite',
            }}
          />

          <div
            className="absolute bottom-1/3 left-1/4 w-[400px] h-[300px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(139,0,0,0.1) 0%, transparent 70%)',
              filter: 'blur(80px)',
              animation: 'glow-b 22s ease-in-out infinite',
            }}
          />

          {/* Scanline texture */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(204,0,0,0.03) 3px, rgba(204,0,0,0.03) 4px)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full flex flex-col items-center text-center">
          {/* Status badge */}
          <div className="anim-in-1 flex items-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-primary/30 bg-primary/5 backdrop-blur-sm">
              <span
                className="w-2 h-2 rounded-full bg-primary"
                style={{ animation: 'pulse-red 2s ease-in-out infinite' }}
              />

              <span className="font-mono text-xs text-primary tracking-widest uppercase">
                Available for Sessions — {monthYear}
              </span>
            </div>
          </div>

          {/* Massive headline */}
          <div className="anim-in-2 mb-4">
            <h1
              className="font-display font-extrabold uppercase leading-none tracking-tighter"
              style={{ fontSize: 'clamp(3rem, 10vw, 9rem)', lineHeight: '0.88' }}
            >
              <span className="text-shimmer block">Drums, mixing</span>
              <span
                className="block mt-2"
                style={{
                  color: '#CC0000',
                  textShadow: '0 0 60px rgba(204,0,0,0.4), 0 0 120px rgba(204,0,0,0.2)',
                }}
              >
                and production.
              </span>
            </h1>
          </div>

          {/* Waveform */}
          <div className="anim-in-3 my-8 flex justify-center">
            <WaveformVisualizer />
          </div>

          {/* Subheadline */}
          <p className="anim-in-3 font-body text-lg md:text-xl text-muted max-w-2xl leading-relaxed mb-10">
            Metal mixing engineer. Session drummer.{' '}
            <span className="text-fg-base/80">Diggity Glover</span> delivers production that hits as
            hard as the music demands.
          </p>

          {/* CTAs */}
          <div className="anim-in-4 flex flex-col sm:flex-row gap-4 items-center">
            <Link href="/contact" className="btn-primary px-8 py-4 text-sm flex items-center gap-3">
              Book a Session
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10M7 2l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <Link href="/about" className="btn-outline px-8 py-4 text-sm">
              Learn More
            </Link>
          </div>

          {/* Scroll indicator */}
          <div className="anim-in-5 mt-16 flex flex-col items-center gap-2">
            <span className="font-mono text-xs text-muted/60 tracking-widest uppercase">
              Scroll
            </span>
            <div className="w-px h-12 bg-gradient-to-b from-primary/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* ─── CREDITS MARQUEE ─── */}
      <div className="border-y border-primary/10 bg-bg-card/50 py-4 overflow-hidden relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-bg-base to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-bg-base to-transparent z-10 pointer-events-none" />
        <div className="animate-marquee flex gap-12 items-center">
          {[...creditsBand, ...creditsBand].map((band, i) => (
            <span key={i} className="flex items-center gap-4 whitespace-nowrap">
              <span className="font-mono text-xs text-muted tracking-widest uppercase">{band}</span>
              <span className="w-1 h-1 rounded-full bg-primary/40" />
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
              SERVICES BENTO GRID
           ═══════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="reveal mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="section-label block mb-3">// Services</span>
              <h2
                className="font-display font-extrabold uppercase tracking-tighter"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1' }}
              >
                What I Do
              </h2>
            </div>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {/* Large tile — Mixing */}
            <div className="reveal service-card p-8 md:row-span-2 flex flex-col justify-between min-h-64">
              <div>
                <div className="text-primary mb-6">{services[0].icon}</div>
                <span className="font-mono text-xs text-muted/60 tracking-widest block mb-3">
                  {services[0].label}
                </span>
                <h3 className="font-display font-bold text-xl text-fg-base mb-4">
                  {services[0].title}
                </h3>
                <p className="font-body text-sm text-muted leading-relaxed">
                  {services[0].description}
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-primary/10 flex items-center justify-between">
                <Link
                  href="/contact"
                  className="font-mono text-xs text-muted hover:text-primary transition-colors flex items-center gap-1 uppercase tracking-wider"
                >
                  Inquire
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5h6M5 2l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Session Drums */}
            <div className="reveal service-card p-8 flex flex-col justify-between min-h-48">
              <div>
                <div className="text-primary mb-4">{services[1].icon}</div>
                <span className="font-mono text-xs text-muted/60 tracking-widest block mb-2">
                  {services[1].label}
                </span>
                <h3 className="font-display font-bold text-lg text-fg-base mb-3">
                  {services[1].title}
                </h3>
                <p className="font-body text-xs text-muted leading-relaxed">
                  {services[1].description}
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <Link
                  href="/contact"
                  className="font-mono text-xs text-muted hover:text-primary transition-colors flex items-center gap-1 uppercase tracking-wider"
                >
                  Inquire{' '}
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5h6M5 2l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Post Production */}
            <div className="reveal service-card p-8 flex flex-col justify-between min-h-48">
              <div>
                <div className="text-primary mb-4">{services[2].icon}</div>
                <span className="font-mono text-xs text-muted/60 tracking-widest block mb-2">
                  {services[2].label}
                </span>
                <h3 className="font-display font-bold text-lg text-fg-base mb-3">
                  {services[2].title}
                </h3>
                <p className="font-body text-xs text-muted leading-relaxed">
                  {services[2].description}
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <Link
                  href="/contact"
                  className="font-mono text-xs text-muted hover:text-primary transition-colors flex items-center gap-1 uppercase tracking-wider"
                >
                  Inquire{' '}
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5h6M5 2l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
              PORTFOLIO / CREDITS
           ═══════════════════════════════════════════════════════ */}
      <section className="py-24 border-t border-primary/10 relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="reveal mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="section-label block mb-3">// Portfolio</span>
              <h2
                className="font-display font-extrabold uppercase tracking-tighter"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1' }}
              >
                Selected Works
              </h2>
            </div>
            <p className="font-body text-muted text-sm max-w-xs">
              A selection of recent mixing, mastering, and session credits.
            </p>
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {portfolioWorks.map((work, i) => (
              <a
                key={`${work.title}-${i}`}
                href={work.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`reveal portfolio-card group`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="aspect-square overflow-hidden">
                  <AppImage
                    src={work.image}
                    alt={work.alt}
                    width={400}
                    height={400}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="overlay" />
                <div className="card-info">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display font-bold text-fg-base text-base mb-1">
                        {work.title}
                      </h3>
                      <p className="font-body text-sm text-fg-base/70">{work.band}</p>
                    </div>
                    <span className="font-mono text-xs text-primary bg-primary/10 border border-primary/20 px-2 py-1">
                      {work.year}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="font-mono text-xs text-muted">{work.role}</span>
                    <span className="w-1 h-1 rounded-full bg-muted/40" />
                    <span className="font-mono text-xs text-muted">{work.genre}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
              TESTIMONIALS
           ═══════════════════════════════════════════════════════ */}
      <section className="py-24 border-t border-primary/10 bg-bg-card/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal mb-14 text-center">
            <span className="section-label block mb-3">// Client Feedback</span>
            <h2
              className="font-display font-extrabold uppercase tracking-tighter"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1' }}
            >
              What Bands Say
            </h2>
          </div>

          {/* Auto-scrolling carousel — the track renders the testimonials
              twice so the -50% loop is seamless. Pauses on hover. */}
          <div className="testimonial-marquee reveal overflow-hidden">
            <div className="testimonial-track gap-1">
              {[...testimonials, ...testimonials].map((t, i) => (
                <div
                  key={`${t.name}-${i}`}
                  aria-hidden={i >= testimonials.length}
                  className="testimonial-card p-8 flex flex-col shrink-0"
                  style={{ width: 'clamp(300px, 80vw, 420px)' }}
                >
                  <p className="font-body text-sm text-fg-base/80 leading-relaxed mb-8 relative z-10 flex-1">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-primary/10">
                    <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden shrink-0">
                      {t.logo ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={t.logo}
                          alt={`${t.band} logo`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-display font-bold text-primary text-sm">
                          {t.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-display font-semibold text-fg-base text-sm">{t.name}</p>
                      <p className="font-mono text-xs text-muted">{t.band}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
              HARD CTA
           ═══════════════════════════════════════════════════════ */}
      <section className="py-24 border-t border-primary/10 relative overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/images/studio-main.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.75)' }}
        />
        {/* Background glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(204,0,0,0.1) 0%, transparent 70%)',
          }}
        />

        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, rgba(204,0,0,0.03) 0px, rgba(204,0,0,0.03) 1px, transparent 1px, transparent 80px)',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="reveal">
            <span className="section-label block mb-6">// Book Now</span>
            <h2
              className="font-display font-extrabold uppercase tracking-tighter mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: '0.95' }}
            >
              Your Next Record <span className="text-gradient-red">Deserves</span>
              <br />
              the Best.
            </h2>
            <p className="font-body text-muted text-lg mb-10 max-w-xl mx-auto">
              Limited availability. Whether you need a full mix, session drums, or end-to-end
              production.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/contact"
                className="btn-primary px-10 py-5 text-sm flex items-center gap-3"
              >
                Start Your Project
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 7h10M7 2l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <Link href="/about" className="btn-outline px-10 py-5 text-sm">
                About Diggity
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
